import React, { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  DeleteOutlined,
  PlusOutlined,
  UploadOutlined,
} from '@ant-design/icons';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  Alert,
  AutoComplete,
  Button,
  Col,
  Modal,
  Row,
  Select,
  Space,
  Table,
  Typography,
  Upload,
  message,
} from 'antd';
import { API } from '@/services/api.service';
import { formatAmount } from './format';

const { Text } = Typography;

/**
 * One editable list of "source → related-party code" rows.
 */
function CodeTable({
  title,
  hint,
  rows,
  onChange,
  sourceKey,
  sourceOptions,
  codeOptions,
  t,
}) {
  const update = (index, patch) =>
    onChange(rows.map((row, i) => (i === index ? { ...row, ...patch } : row)));

  const columns = [
    {
      title: sourceKey === 'partner' ? t('fccs.partner') : t('fccs.account'),
      key: 'source',
      render: (_, row, index) => (
        <Select
          showSearch
          style={{ width: '100%' }}
          value={row[sourceKey] || undefined}
          placeholder={t('fccs.pick')}
          optionFilterProp="label"
          options={sourceOptions}
          onChange={(value) => update(index, { [sourceKey]: value })}
        />
      ),
    },
    {
      title: t('fccs.code'),
      key: 'code',
      width: 220,
      render: (_, row, index) => (
        <AutoComplete
          style={{ width: '100%' }}
          value={row.code}
          options={codeOptions}
          placeholder="88888"
          filterOption={(input, option) =>
            option.label.toLowerCase().includes(input.toLowerCase())
          }
          onChange={(value) => update(index, { code: value })}
        />
      ),
    },
    {
      key: 'remove',
      width: 48,
      render: (_, __, index) => (
        <Button
          size="small"
          shape="circle"
          danger
          icon={<DeleteOutlined />}
          onClick={() => onChange(rows.filter((_, i) => i !== index))}
        />
      ),
    },
  ];

  return (
    <div>
      <Text strong>{title}</Text>
      <div style={{ fontSize: 12, opacity: 0.7, marginBottom: 6 }}>{hint}</div>
      <Table
        rowKey={(_, index) => index}
        size="small"
        pagination={false}
        columns={columns}
        dataSource={rows}
        scroll={{ y: 360 }}
      />
      <Button
        type="dashed"
        shape="round"
        block
        icon={<PlusOutlined />}
        style={{ marginTop: 6 }}
        onClick={() =>
          onChange([...rows, { [sourceKey]: undefined, code: '' }])
        }
      >
        {t('fccs.addRow')}
      </Button>
    </div>
  );
}

/**
 * Which related-party code a balance carries in the FCCS upload: a partner's
 * code wins, then the account's. Can be learned from an upload already sent.
 *
 * @param {Object} props - Component props
 * @param {string} props.companyId - Company
 * @param {Function} props.onSaved - Called after the codes are saved
 * @returns {JSX.Element} The editor
 */
export default function FccsCodesPanel({ companyId, onSaved }) {
  const { t } = useTranslation('clientReports');
  const queryClient = useQueryClient();

  const [partnerCodes, setPartnerCodes] = useState([]);
  const [accountCodes, setAccountCodes] = useState([]);
  const [dirty, setDirty] = useState(false);
  const [imported, setImported] = useState(null);

  const { data: configData } = useQuery({
    queryKey: ['client-reports-fccs-config', companyId],
    queryFn: () => API.clientReports.getFccsConfig(companyId),
  });
  const { data: refData } = useQuery({
    queryKey: ['client-reports-fccs-reference', companyId],
    queryFn: () => API.clientReports.getFccsReference(companyId),
  });

  useEffect(() => {
    if (!configData?.data) return;
    setPartnerCodes(configData.data.partnerCodes || []);
    setAccountCodes(configData.data.accountCodes || []);
    setDirty(false);
  }, [configData]);

  const reference = refData?.data;

  const partnerOptions = useMemo(
    () =>
      (reference?.partners || []).map((p) => ({
        value: p.code,
        label: `${p.code} — ${p.name}`,
      })),
    [reference]
  );
  const accountOptions = useMemo(
    () =>
      (reference?.accounts || []).map((a) => ({
        value: a.account,
        label: `${a.account} — ${a.name}`,
      })),
    [reference]
  );
  const codeOptions = useMemo(
    () =>
      (reference?.relatedParties || []).map((p) => ({
        value: p.code,
        label: `${p.code} — ${p.name}`,
      })),
    [reference]
  );

  const saveMutation = useMutation({
    mutationFn: () =>
      API.clientReports.saveFccsConfig(companyId, {
        partnerCodes,
        accountCodes,
      }),
    onSuccess: () => {
      setDirty(false);
      queryClient.invalidateQueries({
        queryKey: ['client-reports-fccs-config', companyId],
      });
      message.success(t('fccs.saved'));
      onSaved?.();
    },
    onError: (e) => message.error(e?.response?.data?.message || e.message),
  });

  const importMutation = useMutation({
    mutationFn: (file) => API.clientReports.importFccsConfig(companyId, file),
    onSuccess: (result) => setImported(result.data),
    onError: (e) => message.error(e?.response?.data?.message || e.message),
  });

  const applyImport = () => {
    setPartnerCodes(imported.config.partnerCodes);
    setAccountCodes(imported.config.accountCodes);
    setDirty(true);
    setImported(null);
  };

  const edit = (setter) => (rows) => {
    setter(rows);
    setDirty(true);
  };

  return (
    <div>
      <Space wrap style={{ display: 'flex', marginBottom: 12 }}>
        <Upload
          accept=".xlsx"
          showUploadList={false}
          beforeUpload={(file) => {
            importMutation.mutate(file);
            return false;
          }}
        >
          <Button
            shape="round"
            icon={<UploadOutlined />}
            loading={importMutation.isPending}
          >
            {t('fccs.importButton')}
          </Button>
        </Upload>
        <Button
          type="primary"
          className="btn-primary"
          disabled={!dirty}
          loading={saveMutation.isPending}
          onClick={() => saveMutation.mutate()}
        >
          {t('fccs.save')}
        </Button>
        {dirty && <Text type="warning">{t('fccs.unsaved')}</Text>}
      </Space>

      <Row gutter={24}>
        <Col xs={24} lg={12}>
          <CodeTable
            t={t}
            title={t('fccs.partnerCodes')}
            hint={t('fccs.partnerCodesHint')}
            rows={partnerCodes}
            onChange={edit(setPartnerCodes)}
            sourceKey="partner"
            sourceOptions={partnerOptions}
            codeOptions={codeOptions}
          />
        </Col>
        <Col xs={24} lg={12}>
          <CodeTable
            t={t}
            title={t('fccs.accountCodes')}
            hint={t('fccs.accountCodesHint')}
            rows={accountCodes}
            onChange={edit(setAccountCodes)}
            sourceKey="account"
            sourceOptions={accountOptions}
            codeOptions={codeOptions}
          />
        </Col>
      </Row>

      <Modal
        open={Boolean(imported)}
        title={t('fccs.importTitle')}
        width={760}
        onCancel={() => setImported(null)}
        onOk={applyImport}
        okText={t('fccs.importApply')}
        cancelText={t('fccs.cancel')}
        okButtonProps={{ className: 'btn-primary' }}
        cancelButtonProps={{ shape: 'round' }}
        // `.btn` (globals.css) makes the primary button a flex block, so the
        // footer's text-align no longer keeps the two buttons on one line.
        classNames={{ footer: 'flex justify-end gap-2' }}
      >
        {imported && (
          <>
            <p>
              {t('fccs.importSummary', {
                period: `${String(imported.month).padStart(2, '0')}.${imported.year}`,
                partners: imported.config.partnerCodes.length,
                accounts: imported.config.accountCodes.length,
              })}
            </p>
            <Alert
              type={
                imported.comparison.differences.length ? 'warning' : 'success'
              }
              showIcon
              style={{ marginBottom: 12 }}
              message={t('fccs.importMatch', {
                matching: imported.comparison.matching,
                total: imported.comparison.fileRows,
              })}
            />
            {imported.comparison.differences.length > 0 && (
              <Table
                size="small"
                pagination={false}
                rowKey={(r) => `${r.account}|${r.code}`}
                dataSource={imported.comparison.differences}
                columns={[
                  {
                    title: t('fccs.account'),
                    dataIndex: 'account',
                    key: 'account',
                    width: 110,
                  },
                  {
                    title: t('fccs.code'),
                    dataIndex: 'code',
                    key: 'code',
                    width: 90,
                  },
                  {
                    title: t('fccs.inFile'),
                    dataIndex: 'inFile',
                    key: 'inFile',
                    align: 'right',
                    render: (v) => (v === null ? '—' : formatAmount(v)),
                  },
                  {
                    title: t('fccs.built'),
                    dataIndex: 'built',
                    key: 'built',
                    align: 'right',
                    render: (v) => (v === null ? '—' : formatAmount(v)),
                  },
                ]}
              />
            )}
            {imported.unresolved.length > 0 && (
              <Alert
                type="warning"
                showIcon
                style={{ marginTop: 12 }}
                message={t('fccs.importUnresolved', {
                  list: imported.unresolved
                    .map((u) => `${u.account} (${u.code})`)
                    .join(', '),
                })}
              />
            )}
            <p style={{ marginTop: 12, fontSize: 12, opacity: 0.7 }}>
              {t('fccs.importNote')}
            </p>
          </>
        )}
      </Modal>
    </div>
  );
}
